// Per-viewer preferences. Logged-in users keep them in their profile settings;
// this mirror in localStorage makes them apply before /me has loaded.
import { useSyncExternalStore } from "react";

export type Prefs = {
  sound: boolean;
  coordinates: boolean;
  theme: "system" | "light" | "dark";
  board_theme: "wood" | "green" | "blue";
  difficulty: "easier" | "normal" | "harder";
  // move input (move-input-spec §1)
  move_assist: "beginner" | "advanced";
  move_drag: boolean;
  move_targets: boolean;
  /** After a solved exercise, go on to the next one by itself. */
  auto_next: boolean;
  /** Rated training on one label only; empty: all combinations. */
  train_label: string;
};

const DEFAULTS: Prefs = {
  sound: true,
  coordinates: false,
  theme: "system",
  board_theme: "wood",
  difficulty: "normal",
  move_assist: "advanced",
  move_drag: true,
  move_targets: true,
  train_label: "",
  auto_next: true,
};
let current: Prefs = load();
const listeners = new Set<() => void>();

function load(): Prefs {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("dt.prefs") ?? "{}") };
  } catch {
    return { ...DEFAULTS };
  }
}

export function applyPrefs(p: Prefs = current) {
  const root = document.documentElement;
  if (p.theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", p.theme);
  if (p.board_theme === "wood") root.removeAttribute("data-board");
  else root.setAttribute("data-board", p.board_theme);
}

export function setPrefs(patch: Partial<Prefs>) {
  current = { ...current, ...patch };
  try {
    localStorage.setItem("dt.prefs", JSON.stringify(current));
  } catch {
    /* ignore */
  }
  applyPrefs();
  listeners.forEach((fn) => fn());
}

export const getPrefs = () => current;

export function usePrefs(): Prefs {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
  );
}
